import React, { useState, useMemo } from 'react';
import { useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Button, Input, Badge, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Alert, AlertDescription, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@nimbitlabs/ui';
import { FileText, Search, Download, AlertCircle, Calendar, X } from 'lucide-react';
import { format } from 'date-fns';
import { useAuditStore } from './auditStore';

interface AuditLogProps {
  logs?: any[];
  loading?: boolean;
  onExport?: () => Promise<void>;
  onFilterChange?: (filters: any) => void;
}

export function AuditLog({
  logs: propLogs,
  loading: propLoading,
  onExport,
  onFilterChange,
}: AuditLogProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [resourceFilter, setResourceFilter] = useState('all');
  const [selectedLog, setSelectedLog] = useState<any>(null);

  // Fetch audit logs if not provided as props
  const { logs: fetchedLogs, loading: isFetching, fetchAuditLogs } = useAuditStore();

  useEffect(() => {
    if (!propLogs) {
      fetchAuditLogs();
    }
  }, [propLogs, fetchAuditLogs]);
  
  // Use prop logs if provided, otherwise use fetched logs
  const logs = propLogs || fetchedLogs || [];
  const loading = propLoading !== undefined ? propLoading : isFetching;

  const getActionBadgeVariant = (action: string) => {
    const variants: Record<string, any> = {
      create: 'default',
      update: 'secondary',
      delete: 'destructive',
      deploy: 'default',
      rollback: 'outline',
      config_change: 'secondary',
      access_granted: 'default',
      access_revoked: 'destructive',
    };
    return variants[action] || 'outline';
  };

  // Smart detail renderer - unpacks objects into readable fields
  const renderDetailField = (key: string, value: any, depth = 0): React.ReactNode => {
    if (value === null || value === undefined) {
      return <span className="text-muted-foreground italic">null</span>;
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
      return (
        <div className={depth > 0 ? 'ml-4 mt-2' : ''}>
          {Object.entries(value).map(([k, v]) => (
            <div key={k} className="mb-2">
              <span className="text-sm font-medium text-muted-foreground">{k}: </span>
              {renderDetailField(k, v, depth + 1)}
            </div>
          ))}
        </div>
      );
    }

    if (Array.isArray(value)) {
      return (
        <div className={depth > 0 ? 'ml-4 mt-1' : ''}>
          {value.map((item, idx) => (
            <div key={idx} className="mb-1">
              <span className="text-xs text-muted-foreground">[{idx}]</span> {renderDetailField(`${idx}`, item, depth + 1)}
            </div>
          ))}
        </div>
      );
    }

    if (typeof value === 'boolean') {
      return <Badge variant={value ? 'default' : 'outline'}>{value.toString()}</Badge>;
    }

    if (typeof value === 'string' && value.length > 100) {
      return <p className="text-sm break-words">{value}</p>;
    }

    return <span className="text-sm">{String(value)}</span>;
  };

  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      const matchesSearch = !searchQuery || 
        log.userName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        log.resourceName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        log.action?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesAction = actionFilter === 'all' || log.action === actionFilter;
      const matchesResource = resourceFilter === 'all' || log.resource === resourceFilter;
      return matchesSearch && matchesAction && matchesResource;
    });
  }, [logs, searchQuery, actionFilter, resourceFilter]);

  React.useEffect(() => {
    if (onFilterChange) {
      onFilterChange({
        action: actionFilter !== 'all' ? actionFilter : undefined,
        resource: resourceFilter !== 'all' ? resourceFilter : undefined,
        searchTerm: searchQuery || undefined,
      });
    }
  }, [actionFilter, resourceFilter, searchQuery, onFilterChange]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <FileText className="h-8 w-8" />
            Audit Log
          </h1>
          <p className="text-muted-foreground mt-1">
            Track all system activities and changes
          </p>
        </div>
        <Button onClick={onExport} disabled={!onExport}>
          <Download className="h-4 w-4 mr-2" />
          Export
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Events</p>
                <p className="text-3xl font-bold mt-2">{logs.length}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                <FileText className="h-6 w-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Today</p>
                <p className="text-3xl font-bold mt-2">
                  {logs.filter(l => {
                    const today = new Date().setHours(0, 0, 0, 0);
                    return new Date(l.timestamp).getTime() >= today;
                  }).length}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-500/10 flex items-center justify-center">
                <Calendar className="h-6 w-6 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Unique Users</p>
                <p className="text-3xl font-bold mt-2">
                  {new Set(logs.map(l => l.userId)).size}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-purple-500/10 flex items-center justify-center">
                <FileText className="h-6 w-6 text-purple-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Critical Events</p>
                <p className="text-3xl font-bold mt-2">
                  {logs.filter(l => l.action === 'delete' || l.action === 'access_revoked').length}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-red-500/10 flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-red-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search logs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Filter by action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="create">Create</SelectItem>
                <SelectItem value="update">Update</SelectItem>
                <SelectItem value="delete">Delete</SelectItem>
                <SelectItem value="deploy">Deploy</SelectItem>
                <SelectItem value="config_change">Config Change</SelectItem>
              </SelectContent>
            </Select>
            <Select value={resourceFilter} onValueChange={setResourceFilter}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Filter by resource" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Resources</SelectItem>
                <SelectItem value="service">Service</SelectItem>
                <SelectItem value="environment">Environment</SelectItem>
                <SelectItem value="team">Team</SelectItem>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="deployment">Deployment</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>Loading audit logs...</p>
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No audit logs found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Resource</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLogs.map((log) => (
                    <TableRow 
                      key={log.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => setSelectedLog(log)}
                    >
                      <TableCell className="text-sm text-muted-foreground">
                        {format(new Date(log.timestamp), 'MMM d, yyyy h:mm a')}
                      </TableCell>
                      <TableCell className="font-medium">{log.userName}</TableCell>
                      <TableCell>
                        <Badge variant={getActionBadgeVariant(log.action)}>
                          {log.action.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{log.resourceName}</div>
                          <div className="text-xs text-muted-foreground capitalize">
                            {log.resource}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-md">
                        <div className="text-sm text-muted-foreground truncate">
                          {JSON.stringify(log.details)}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!selectedLog} onOpenChange={(open) => !open && setSelectedLog(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Audit Log Detail
            </DialogTitle>
            <DialogDescription>
              Complete information about this audit event
            </DialogDescription>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-6">
              {/* Overview */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Timestamp</p>
                  <p className="text-sm mt-1">{format(new Date(selectedLog.timestamp), 'PPpp')}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Action</p>
                  <div className="mt-1">
                    <Badge variant={getActionBadgeVariant(selectedLog.action)}>
                      {selectedLog.action.replace('_', ' ')}
                    </Badge>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">User</p>
                  <p className="text-sm mt-1">{selectedLog.userName}</p>
                  <p className="text-xs text-muted-foreground">{selectedLog.userId}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Resource</p>
                  <p className="text-sm mt-1">{selectedLog.resourceName}</p>
                  <p className="text-xs text-muted-foreground capitalize">{selectedLog.resource}</p>
                </div>
              </div>

              {/* Details - Smart Rendering */}
              {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-3">Event Details</p>
                  <Card className="bg-muted/50">
                    <CardContent className="pt-4">
                      {renderDetailField('details', selectedLog.details)}
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Show raw JSON as fallback if details is empty or for debugging */}
              {(!selectedLog.details || Object.keys(selectedLog.details).length === 0) && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-3">Raw Event Data</p>
                  <Card className="bg-muted/50">
                    <CardContent className="pt-4">
                      <pre className="text-xs overflow-x-auto">
                        {JSON.stringify(selectedLog, null, 2)}
                      </pre>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Metadata */}
              {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-3">Additional Metadata</p>
                  <Card className="bg-muted/50">
                    <CardContent className="pt-4">
                      {renderDetailField('metadata', selectedLog.metadata)}
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* IP Address / Location if available */}
              {(selectedLog.ipAddress || selectedLog.userAgent) && (
                <div className="grid grid-cols-1 gap-4">
                  {selectedLog.ipAddress && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">IP Address</p>
                      <p className="text-sm mt-1 font-mono">{selectedLog.ipAddress}</p>
                    </div>
                  )}
                  {selectedLog.userAgent && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">User Agent</p>
                      <p className="text-sm mt-1 text-muted-foreground">{selectedLog.userAgent}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
